import React from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import classNames from 'classnames';
import _ from 'lodash';
import { ControlLabel } from 'react-bootstrap';
import { injectIntl, FormattedMessage } from 'react-intl';

import styles from './AutoAddress.module.css';
import {
    verifyUKPostcode,
    verifyUKCrownPostcode
} from '../../../helpers/addressHelpers';
import { nonAlphaSpaces } from '../../../helpers/helpers';
import DropDown from '../../DropDown/';
import InputTooltip from '../../InputTooltip/InputToolTip';
import InternalLink from '../../InternalLink';
import messages from '../Address.messages';

class AutoAddress extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            address: '',
            searchable: true,
            addressSelected: false,
            isPostcodeValid: false
        };
        this.setRef = React.createRef();
    }

    componentDidMount() {
        const {
            field: { auto },
            manualAddressState
        } = this.props;
        auto.onChange(!manualAddressState);
    }

    searchAddress = (value) => {
        value = value.replace(/\s/g, '');
        this.props.onAddressChange(value);
    };

    handleOnChange = (searchTerm) => {
        this.setState({
            isPostcodeValid: verifyUKPostcode(searchTerm)
        });
        this.searchAddressOnCorrectPostcode(searchTerm);
    };

    searchAddressOnCorrectPostcode = (searchTerm) => {
        const postcodeIsValidAndNotCrownDependency =
            verifyUKPostcode(searchTerm) && !verifyUKCrownPostcode(searchTerm);
        if (postcodeIsValidAndNotCrownDependency) {
            this.searchAddress(searchTerm);
        }
    };

    onSelect = (item) => {
        const { onChange, addressIndex, fetchedAddresses } = this.props;
        let address;

        if (item) {
            address = _.find(fetchedAddresses.list, {
                equifax_token: item.value
            });
        }

        tracking.ampli.formFieldCompleted({
            form_field: 'auto_address'
        });

        address = address ? address : {};
        onChange(addressIndex, address);
    };

    getAddresses = () => {
        const { fetchedAddresses: addresses } = this.props;

        if (_.isPlainObject(addresses.list)) {
            return [];
        }

        return _.map(addresses.list, (address) => {
            return {
                label: `${this.buildAddressLabel(address)}`,
                value: address.equifax_token
            };
        });
    };

    renderCrownDependencyPostcodeMessage = () => {
        return (
            <div className="no-results-custom">
                <span className="head">
                    {this.props.intl.formatMessage(
                        messages.CrownDependencyPostcodeHeading
                    )}
                </span>
                <span className="body">
                    Unfortunately, we are currently unable to lend to applicants
                    based in Guernsey, Jersey or the Isle of Man
                </span>
            </div>
        );
    };

    renderInvalidPostcodeMessage = () => {
        const { intl } = this.props;
        return (
            <div className="no-results-custom">
                <span className="head">
                    {intl.formatMessage(messages.noResultsHeading)}
                </span>
                <span className="body">
                    {intl.formatMessage(messages.noResultsBody)}
                </span>
            </div>
        );
    };

    returnPostcodeErrorMessage = (value) => {
        if (verifyUKCrownPostcode(value)) {
            return this.renderCrownDependencyPostcodeMessage();
        } else {
            return this.renderInvalidPostcodeMessage();
        }
    };

    renderNoResults = (value) => {
        return this.returnPostcodeErrorMessage(value);
    };

    warningRenderer = () => {
        const { onManualAddress, addressIndex, personUID } = this.props;

        return (
            <div className="no-results-custom">
                <span className="head">Oops. Something went wrong</span>
                <span className="body">
                    Please try to enter your address{' '}
                    <InternalLink
                        onClick={onManualAddress.bind(
                            null,
                            personUID,
                            addressIndex
                        )}>
                        manually
                    </InternalLink>
                </span>
            </div>
        );
    };

    buildAddressLabel = (address) => {
        const { house_name, house_number, street_line_1, town, postcode } =
            address;

        const addressString =
            `${house_name}, ${house_number} ${street_line_1}, ${town}, ${postcode}`
                .split(',')
                .map((l) => l.trim())
                .filter((l) => l)
                .join(', ');

        return `${addressString}`;
    };

    getSelectedLabel = () => {
        const { field } = this.props;

        if (field && field.equifax_token.value) {
            let {
                house_name: { value: house_name },
                house_number: { value: house_number },
                street_line_1: { value: street_line_1 },
                town: { value: town },
                postcode: { value: postcode }
            } = field;

            let address = {
                house_name,
                house_number,
                street_line_1,
                town,
                postcode
            };

            return this.buildAddressLabel(address);
        }
    };

    renderNoSearchTerm = () => {
        const { intl } = this.props;
        return (
            <div className="no-results-custom">
                <span className="head">
                    {intl.formatMessage(messages.enterPostcode)}
                </span>
                <span className="body">
                    {intl.formatMessage(messages.enterPostcodeSubtext)}
                </span>
            </div>
        );
    };

    shouldDisplayMenuMessageOnBlur = () => {
        return !this.state.isPostcodeValid;
    };

    render() {
        const {
            addressIndex,
            field,
            onManualAddress,
            fetchedAddresses,
            personUID,
            intl
        } = this.props;

        const inValid = (field) => {
            return classNames(styles.addressSearch, 'fs-exclude', {
                invalid: field.touched && field.error
            });
        };

        const error = field.equifax_token.error;
        return (
            <div ref={this.setRef} className={inValid(field.equifax_token)}>
                <ControlLabel data-testid="home-address-label">
                    Home
                    <div className="tooltip-holder">
                        {' '}
                        address
                        <InputTooltip
                            overlayText={intl.formatMessage(
                                messages.enterPostcodeSubtext
                            )}
                        />
                    </div>
                </ControlLabel>
                {error ? (
                    <div className="error" data-testid="address-search-error">
                        <FormattedMessage id={error} defaultMessage={error} />
                    </div>
                ) : null}
                <DropDown.Async
                    data-dd-privacy="mask"
                    className="address-lookup fs-exclude"
                    items={this.getAddresses()}
                    value={field.equifax_token.value}
                    onChange={this.handleOnChange}
                    onButtonClick={this.searchAddress}
                    onBlur={this.searchAddress}
                    selectedLabel={this.getSelectedLabel()}
                    onSelect={this.onSelect}
                    isLoading={fetchedAddresses.fetching}
                    isSuccess={fetchedAddresses.success}
                    helperText={'Choose your address (or'}
                    switchPosition={'top'}
                    manualSwitchText={'enter manually)'}
                    manualSwitch={onManualAddress.bind(
                        null,
                        personUID,
                        addressIndex
                    )}
                    placeholder={intl.formatMessage(
                        messages.searchPostcodePlaceholder
                    )}
                    formatter={nonAlphaSpaces}
                    noResultsRenderer={this.renderNoResults}
                    noSearchTermRenderer={this.renderNoSearchTerm}
                    warningRenderer={this.warningRenderer}
                    shouldDisplayMenuMessageOnBlur={this.shouldDisplayMenuMessageOnBlur()}
                />
                <div className="toggle-role">
                    <InternalLink
                        data-testid="auto-address-change-to-manual-entry"
                        onClick={onManualAddress.bind(
                            null,
                            personUID,
                            addressIndex
                        )}>
                        <span className={styles.toggleAddressManually}>
                            Enter address manually
                        </span>
                    </InternalLink>
                </div>
            </div>
        );
    }
}

export default injectIntl(AutoAddress);
